import { useSyncExternalStore } from "react";
import { readValue, storageKeys, writeValue } from "./device-storage";
import { detectPlatform, type InstallPlatform } from "./platform";

// платформа — в `platform.ts` (без React: её берёт и service worker); здесь — для прежних импортов
export { detectPlatform, type InstallPlatform };

/**
 * Установка приложения (research R5, FR-020–FR-022, data-model.md `InstallState`).
 *
 * Модуль не зависит от моделей мобильной версии (`src/mobile/models`): его же использует
 * предложение установки в полной версии на компьютере (US5). Состояние — маленькое хранилище
 * с подпиской; в React оно читается хуком `useInstall`.
 */

/** Срок молчания предложения после «Позже» (FR-022) */
export const SNOOZE_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Событие браузера `beforeinstallprompt`: в типах DOM его нет */
export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Результат `prompt()`: `unavailable` — события браузера нет (iPhone, уже использовано) */
export type PromptResult = "accepted" | "dismissed" | "unavailable";

export interface InstallSnapshot {
    platform: InstallPlatform;
    /** Запущено как установленное приложение */
    installed: boolean;
    /** Браузер дал событие установки: кнопка «Установить» вызовет системный диалог */
    canPrompt: boolean;
    /** До какого момента предложение молчит, мс с начала эпохи */
    snoozedUntil: number | null;
    /** Показывать ли предложение установки (над вкладками или на компьютере) */
    offerVisible: boolean;
}

/** Всё, что сервис берёт у браузера; в тестах подменяется */
export interface InstallEnv {
    userAgent: string;
    maxTouchPoints: number;
    /** `display-mode: standalone` или `navigator.standalone` */
    isStandalone: () => boolean;
    /** Подписка на событие окна; возвращает функцию отписки */
    listen: (event: string, handler: (event: Event) => void) => () => void;
    now: () => number;
}

export interface InstallService {
    getSnapshot: () => InstallSnapshot;
    subscribe: (listener: () => void) => () => void;
    /** Показать системный диалог установки */
    prompt: () => Promise<PromptResult>;
    /** «Позже»: предложение молчит 14 дней */
    snooze: () => void;
}

/**
 * Предложение видно, если приложение не установлено, установка возможна (событие браузера
 * или iPhone с инструкцией) и срок «Позже» вышел (data-model.md).
 */
export function isOfferVisible(
    state: Pick<InstallSnapshot, "platform" | "installed" | "canPrompt" | "snoozedUntil">,
    now: number
): boolean {
    return (
        !state.installed &&
        (state.canPrompt || state.platform === "ios") &&
        (state.snoozedUntil === null || now >= state.snoozedUntil)
    );
}

/** Дата «Позже» с устройства; нет или повреждена — `null` */
function readSnooze(): number | null {
    const raw = readValue(storageKeys.installSnoozedUntil, null);
    const time = raw === null ? NaN : Date.parse(raw);

    return Number.isNaN(time) ? null : time;
}

export function createInstallService(env: InstallEnv): InstallService {
    const listeners = new Set<() => void>();
    let deferred: BeforeInstallPromptEvent | null = null;
    let installed = env.isStandalone();
    let snoozedUntil = readSnooze();
    const platform = detectPlatform(env.userAgent, env.maxTouchPoints);

    const build = (): InstallSnapshot => {
        const state = { platform, installed, canPrompt: deferred !== null, snoozedUntil };

        return { ...state, offerVisible: isOfferVisible(state, env.now()) };
    };

    let snapshot = build();

    const update = () => {
        snapshot = build();
        listeners.forEach((listener) => listener());
    };

    // без preventDefault браузер показал бы свою мини-полосу, а событие нельзя было бы отложить
    env.listen("beforeinstallprompt", (event) => {
        event.preventDefault();
        deferred = event as BeforeInstallPromptEvent;
        update();
    });

    env.listen("appinstalled", () => {
        installed = true;
        deferred = null;
        update();
    });

    return {
        getSnapshot: () => snapshot,
        subscribe: (listener) => {
            listeners.add(listener);

            return () => listeners.delete(listener);
        },
        prompt: async () => {
            const event = deferred;

            if (!event) {
                return "unavailable";
            }

            // событие одноразовое: повторный prompt() с ним бросает исключение
            deferred = null;
            update();

            try {
                await event.prompt();

                return (await event.userChoice).outcome;
            } catch {
                return "unavailable";
            }
        },
        snooze: () => {
            snoozedUntil = env.now() + SNOOZE_DAYS * DAY_MS;
            // хранилища нет — «Позже» действует до перезапуска, потом предложение вернётся
            writeValue(storageKeys.installSnoozedUntil, new Date(snoozedUntil).toISOString());
            update();
        },
    };
}

/** Окружение браузера; без `window` (тесты в среде node) — пустое */
export function browserInstallEnv(): InstallEnv {
    return {
        userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
        maxTouchPoints: typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints || 0,
        isStandalone: () => {
            if (typeof window === "undefined") {
                return false;
            }

            const mql = typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;

            return !!mql?.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
        },
        listen: (event, handler) => {
            if (typeof window === "undefined") {
                return () => undefined;
            }

            window.addEventListener(event, handler);

            return () => window.removeEventListener(event, handler);
        },
        now: () => Date.now(),
    };
}

let shared: InstallService | null = null;

/** Общий сервис страницы; создаётся при первом обращении */
export function installService(): InstallService {
    if (!shared) {
        shared = createInstallService(browserInstallEnv());
    }

    return shared;
}

// подписка при загрузке модуля: `beforeinstallprompt` приходит рано, раньше первого экрана
if (typeof window !== "undefined") {
    installService();
}

/** Состояние установки для компонента; перерисовывается при его изменении */
export function useInstall(): InstallSnapshot & Pick<InstallService, "prompt" | "snooze"> {
    const service = installService();
    const snapshot = useSyncExternalStore(service.subscribe, service.getSnapshot, service.getSnapshot);

    return { ...snapshot, prompt: service.prompt, snooze: service.snooze };
}
